import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { useConfirm } from '../components/ConfirmDialog';
import { Card, SectionTitle, StageBadge, Spinner, ErrorNote, Empty } from '../components/ui';
import { fmtDateTime } from '../lib/format';

// Matches the server's per-update cap in src/routes/jobs.js.
const MAX_PHOTOS = 30;
const MAX_PHOTO_MB = 6;

export default function JobTracker() {
  const { id } = useParams();
  const { toast, run } = useApp();
  const [ask, confirmDialog] = useConfirm();
  const fileRef = useRef();

  const { data: project, error, loading, reload } = useAsync(() => api.get('/projects/' + id), [id]);

  const [job, setJob] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);

  // A project reaches this screen before a job record exists; create it once.
  useEffect(() => {
    if (!project) return;
    if (project.job) { setJob(project.job); return; }
    let cancelled = false;
    setJobError(null);
    run(async () => {
      const j = await api.post('/jobs', {
        projectId: id, scheduledStartDate: new Date().toISOString().slice(0, 10),
      });
      if (!cancelled) setJob(j);
    }, { onError: (m) => { if (!cancelled) setJobError(m); } });
    return () => { cancelled = true; };
  }, [project, id, run]);

  // Errors first: with no project there is never a job, so checking for the
  // job first left a failed load spinning forever instead of saying why.
  if (error || jobError) {
    return <div className="view"><ErrorNote onRetry={reload}>{error || jobError}</ErrorNote></div>;
  }
  if (loading || !job) return <Spinner label="Loading job" />;

  function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const room = MAX_PHOTOS - pending.length;
    if (files.length > room) toast(`Only ${room} more photo${room === 1 ? '' : 's'} fit on this update`);
    files.slice(0, Math.max(0, room)).forEach(file => {
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        toast(`${file.name} is over ${MAX_PHOTO_MB}MB`);
        return;
      }
      const fr = new FileReader();
      fr.onload = () => setPending(p => [...p, fr.result]);
      fr.readAsDataURL(file);
    });
  }

  const addUpdate = () => run(async () => {
    if (!note.trim()) return toast('Type an update first');
    setBusy(true);
    const updated = await api.post(`/jobs/${job.id}/progress`, { note: note.trim(), photos: pending });
    setJob(updated);
    setNote(''); setPending([]);
    toast('Update added');
    setBusy(false);
  }, { onError: (m) => { setBusy(false); toast(m); } });

  const start = () => run(async () => {
    await api.post(`/jobs/${job.id}/start`);
    toast('Job started'); reload();
  });

  const complete = async () => {
    if (!await ask({ title: 'Mark this job complete?', body: 'The job will be closed and you can move on to the invoice.', confirmLabel: 'Mark complete', danger: false, icon: '✓' })) return;
    run(async () => {
      await api.post(`/jobs/${job.id}/complete`);
      toast('Job complete'); reload();
    });
  };

  const updates = [...(job.progressUpdates || [])].reverse();

  return (
    <>
      <PageHeader
        title="Job tracker"
        sub={`${project.number} · ${project.customer?.name || ''}`}
        back={`/project/${id}`}
      />
      <div className="view">
        <Card>
          <div className="row between">
            <div><div className="sb">Status</div><StageBadge stage={project.stage} /></div>
            <div style={{ textAlign: 'right' }}>
              <div className="sb">Scheduled</div>
              <div className="nm">{job.scheduledStartDate || '—'}</div>
            </div>
          </div>
        </Card>

        {!job.startedAt ? <button className="btn pri" onClick={start}>▶ Start job</button> : null}
        {job.startedAt && !job.completedAt ? <button className="btn grn" onClick={complete}>✓ Mark job complete</button> : null}

        <Card>
          <SectionTitle>Add a progress update</SectionTitle>
          <textarea className="fi" rows={3} style={{ marginTop: 'var(--s-3)' }}
            aria-label="Progress note"
            placeholder="What happened today? (e.g. tear-off done, shingles delivered…)"
            value={note} onChange={e => setNote(e.target.value)} />

          {pending.length ? (
            <div className="photo-grid" style={{ marginTop: 'var(--s-3)' }}>
              {pending.map((src, i) => (
                <div className="photo-pending" key={i}>
                  <img className="photo-thumb" src={src} alt={`Pending photo ${i + 1}`} />
                  <button className="rm" aria-label={`Remove pending photo ${i + 1}`}
                    onClick={() => setPending(p => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="row" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
            <button type="button" className="btn sm" onClick={() => fileRef.current?.click()}>
              📷 Add photos
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple
              style={{ display: 'none' }} onChange={onPickFiles} />
            <button className="btn sm pri" onClick={addUpdate} disabled={busy}>
              {busy ? 'Saving…' : '＋ Add update'}
            </button>
            <span className="sb">{pending.length}/{MAX_PHOTOS} photos</span>
          </div>
        </Card>

        <Card tight>
          <SectionTitle>Progress log</SectionTitle>
          <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-2)' }}>
            {updates.length ? updates.map((u, i) => (
              <div className="card tight" key={i}>
                <div className="sb">{fmtDateTime(u.at)}</div>
                <p style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-base)' }}>{u.note}</p>
                {(u.photos || []).length ? (
                  <div className="photo-grid" style={{ marginTop: 'var(--s-2)' }}>
                    {u.photos.map((src, j) => (
                      <a key={j} href={src} target="_blank" rel="noopener noreferrer">
                        <img className="photo-thumb" src={src} alt={`Progress photo ${j + 1}`} loading="lazy" />
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            )) : <Empty>No updates yet.</Empty>}
          </div>
        </Card>

        {job.completedAt ? <Link className="btn pri" to={`/project/${id}/invoice`}>Create invoice</Link> : null}
      </div>
      {confirmDialog}
    </>
  );
}
